import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Checkbox, Col, Empty, Input, Modal, Radio, Row, Select, Space, Statistic, Table, Tag, Tooltip, Typography, Upload, message } from 'antd';
import { ReloadOutlined, SwapOutlined, UploadOutlined } from '@ant-design/icons';
import { loadTradeData, saveTradeData, useIsMobile } from './tradeStorage';

const MOBILE_PAGE_SIZE = 30;
const ASSET_SORT_OPTIONS = {
  'value:descend': 'Value: high to low', 'value:ascend': 'Value: low to high',
  'asset:ascend': 'Name A-Z', 'position:ascend': 'Position', 'horizon:ascend': 'Horizon'
};

const { Text, Title } = Typography;
const CURRENT_VALUES_KEY = 'aod.tradeCenter.currentYearValues.v1';
const normalizeID = (value) => {
  const id = String(value || '').trim();
  return /^\d+$/.test(id) ? String(Number.parseInt(id, 10)) : id.toLowerCase();
};
const normalizeAssetID = (value) => {
  const id = String(value || '').trim();
  const pick = id.match(/^FP_(?:\d+_)?(\d{4})_(?:(?:early|mid|late)_(\d+)|(\d+))$/i);
  if (pick) {
    const round = pick[2] != null ? Number(pick[2]) + 1 : Number(pick[3]);
    return `fp_${pick[1]}_${round}`;
  }
  return normalizeID(id);
};
const normalizeName = (value) => {
  const name = String(value || '').trim().toLowerCase();
  const parts = name.split(',').map((part) => part.trim()).filter(Boolean);
  return (parts.length > 1 ? `${parts[1]} ${parts[0]}` : name).replace(/[^a-z0-9]/g, '');
};
const formatValue = (value) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const playerValueID = (player) => normalizeAssetID(player.mflId || player.assetID || player.playerID);
const pickRoundFromName = (name) => {
  const text = String(name || '');
  const round = Number(text.match(/round\s*(\d+)/i)?.[1]);
  if (round) return round;
  const ordinal = text.match(/\b(1st|2nd|3rd|4th)\b/i)?.[1];
  return ordinal ? Number(ordinal[0]) : null;
};
const pickYearRoundKey = (year, round) => year && round ? `${Number(year)}:${Number(round)}` : null;
const pickYearRoundFromAsset = (asset) => {
  const id = String(asset?.assetID || asset?.id || asset?.playerID || '');
  const idMatch = id.match(/^FP_(?:\d+_)?(\d{4})_(?:(?:early|mid|late)_(\d+)|(\d+))$/i);
  const year = Number(asset?.year || idMatch?.[1] || String(asset?.name || '').match(/\b(20\d{2})\b/)?.[1]);
  const round = Number(asset?.round || pickRoundFromName(asset?.name) || (idMatch?.[2] != null ? Number(idMatch[2]) + 1 : idMatch?.[3]));
  return pickYearRoundKey(year, round);
};
const pickYearRoundFromPlayer = (player) => {
  const year = Number(String(player?.name || '').match(/\b(20\d{2})\b/)?.[1]);
  const round = pickRoundFromName(player?.name);
  if (year && round) return pickYearRoundKey(year, round);
  const id = String(player?.mflId || player?.assetID || '');
  return pickYearRoundFromAsset({ assetID: id, name: player?.name });
};
const assetValueID = (asset) => normalizeAssetID(asset?.playerID || asset?.assetID || asset?.id);
export const HORIZON_ORDER = ['VERY_LONG', 'LONG', 'SIMILAR', 'SHORT', 'VERY_SHORT'];
export const HORIZON_LABELS = {
  VERY_LONG: 'Very long-term', LONG: 'Long-term', SIMILAR: 'Similar',
  SHORT: 'Short-term', VERY_SHORT: 'Super short-term', MISSING: 'No comparison'
};
const horizonForPercent = (percent, dynastyValue, position) => {
  if (String(position || '').toUpperCase() === 'PICK') return 'VERY_LONG';
  if (percent == null || !Number.isFinite(percent)) return Number(dynastyValue) > 1000 ? 'LONG' : 'MISSING';
  if (percent <= -0.4) return 'VERY_LONG';
  if (percent < -0.2) return 'LONG';
  if (percent < 0.2) return 'SIMILAR';
  if (percent < 0.4) return 'SHORT';
  return 'VERY_SHORT';
};
export const HorizonTag = ({ horizon }) => {
  const colors = { VERY_LONG: 'blue', LONG: 'cyan', SIMILAR: 'default', SHORT: 'orange', VERY_SHORT: 'red' };
  return horizon && horizon !== 'MISSING' ? <Tag color={colors[horizon]}>{HORIZON_LABELS[horizon]}</Tag> : <Tag>No comparison</Tag>;
};

const parseDelimited = (text) => {
  const rows = [];
  const delimiter = (text.split(/\r?\n/, 1)[0] || '').includes(';') ? ';' : ',';
  let row = [];
  let field = '';
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === '"') {
      if (quoted && text[index + 1] === '"') { field += '"'; index += 1; }
      else quoted = !quoted;
    } else if (char === delimiter && !quoted) { row.push(field.trim()); field = ''; }
    else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && text[index + 1] === '\n') index += 1;
      row.push(field.trim());
      if (row.some(Boolean)) rows.push(row);
      row = [];
      field = '';
    } else field += char;
  }
  row.push(field.trim());
  if (row.some(Boolean)) rows.push(row);
  return rows;
};

const parseCurrentYearCsv = (text) => {
  const rows = parseDelimited(text.replace(/^\uFEFF/, ''));
  if (rows.length < 2) throw new Error('The file has no player rows.');
  const headers = rows[0].map((header) => header.trim().toLowerCase().replace(/[^a-z0-9]/g, ''));
  const nameIndex = headers.indexOf('name');
  const valueIndex = headers.indexOf('value');
  if (nameIndex < 0 || valueIndex < 0) throw new Error('The CSV needs name and value columns.');
  const mflIndex = headers.indexOf('mflid');
  const posIndex = headers.indexOf('position');
  const posRankIndex = headers.findIndex((header) => ['positionrank', 'posrank', 'rankposition', 'rankpos'].includes(header));
  const overallRankIndex = headers.indexOf('overallrank');
  const players = rows.slice(1).map((cells) => {
    const position = cells[posIndex] || '';
    const value = Number(cells[valueIndex]);
    return {
      name: cells[nameIndex] || '', mflId: cells[mflIndex] || '', position,
      value: value * (String(position).toUpperCase() === 'TE' ? 2 : 1),
      positionRank: Number(cells[posRankIndex]) || null,
      overallRank: Number(cells[overallRankIndex]) || null
    };
  }).filter((player) => player.name && Number.isFinite(player.value));
  if (!players.length) throw new Error('No valid player values were found.');
  const byPosition = new Map();
  players.forEach((player) => {
    const position = String(player.position).toUpperCase();
    if (!byPosition.has(position)) byPosition.set(position, []);
    byPosition.get(position).push(player);
  });
  byPosition.forEach((positionPlayers) => {
    positionPlayers.sort((a, b) => b.value - a.value);
    positionPlayers.forEach((player, index) => {
      if (!player.positionRank) player.positionRank = index + 1;
    });
  });
  return players;
};

const addMissingPositionRanks = (rankings) => {
  const byPosition = new Map();
  const copiedRankings = rankings.map((player) => ({ ...player }));
  copiedRankings.forEach((player) => {
    const position = String(player.position || '').toUpperCase();
    if (!position) return;
    if (!byPosition.has(position)) byPosition.set(position, []);
    byPosition.get(position).push(player);
  });
  byPosition.forEach((positionPlayers) => {
    positionPlayers.sort((a, b) => Number(b.value || 0) - Number(a.value || 0));
    positionPlayers.forEach((player, index) => {
      if (!player.positionRank) player.positionRank = index + 1;
    });
  });
  return copiedRankings;
};
const normalizeCurrentYearValue = (position, positionalRank, positionCurves) => {
  const rank = Number(positionalRank);
  const comparablePlayers = (positionCurves.get(String(position || '').toUpperCase()) || [])
    .filter((player) => Math.abs(Number(player.positionRank || 99999) - rank) <= 2);
  if (!comparablePlayers.length) return null;
  return comparablePlayers.reduce((sum, player) => sum + Number(player.value || 0), 0) / comparablePlayers.length;
};

// Adds this-year value, positional ranks, delta, and horizon to each dynasty player.
export const enrichWithCurrentYear = (players, currentPlayers) => {
  const ranked = addMissingPositionRanks(players);
  const curves = new Map();
  ranked.forEach((player) => {
    const position = String(player.position || '').toUpperCase();
    if (!position || !Number.isFinite(Number(player.value))) return;
    if (!curves.has(position)) curves.set(position, []);
    curves.get(position).push(player);
  });
  curves.forEach((list) => list.sort((a, b) => Number(a.positionRank || 99999) - Number(b.positionRank || 99999)));
  const byID = new Map();
  const byName = new Map();
  addMissingPositionRanks(currentPlayers || []).forEach((player) => {
    const current = { value: player.value, positionRank: player.positionRank };
    const id = normalizeID(player.mflId);
    if (id && id !== 'unk') byID.set(id, current);
    byName.set(normalizeName(player.name), current);
  });
  return ranked.map((player) => {
    const assetID = playerValueID(player);
    const current = (assetID && assetID !== 'unk' ? byID.get(assetID) : null) || byName.get(normalizeName(player.name));
    const currentYearValue = current?.value != null && current?.positionRank
      ? normalizeCurrentYearValue(player.position, current.positionRank, curves)
      : null;
    const valueDifference = currentYearValue == null ? null : currentYearValue - Number(player.value || 0);
    const valueDifferencePercent = currentYearValue == null || !Number(player.value) ? null : valueDifference / Number(player.value);
    return {
      ...player, currentYearValue, rawCurrentYearValue: current?.value,
      currentYearPositionRank: current?.positionRank || null,
      valueDifference, valueDifferencePercent,
      horizon: horizonForPercent(valueDifferencePercent, player.value, player.position)
    };
  });
};

// Server copy is the source of truth; a local copy only seeds an empty account.
export const useCurrentYearValues = (userID) => {
  const [currentYearValues, setCurrentYearValues] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CURRENT_VALUES_KEY) || 'null') || { players: [], fileName: null }; }
    catch (_) { return { players: [], fileName: null }; }
  });
  const latestRef = useRef(currentYearValues);
  latestRef.current = currentYearValues;

  useEffect(() => {
    localStorage.setItem(CURRENT_VALUES_KEY, JSON.stringify(currentYearValues));
  }, [currentYearValues]);

  useEffect(() => {
    let active = true;
    loadTradeData(userID, ['currentYear']).then((remote) => {
      if (!active) return;
      if (remote.currentYear?.players?.length) setCurrentYearValues(remote.currentYear);
      else if (latestRef.current.players?.length) saveTradeData(userID, 'currentYear', latestRef.current).catch(() => {});
    }).catch(() => {});
    return () => { active = false; };
  }, [userID]);

  const handleCurrentYearUpload = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const nextPlayers = parseCurrentYearCsv(String(reader.result || ''));
        const next = { players: nextPlayers, fileName: file.name, uploadedAt: new Date().toISOString() };
        setCurrentYearValues(next);
        saveTradeData(userID, 'currentYear', next)
          .then(() => message.success(`Saved ${nextPlayers.length.toLocaleString()} current-year values.`))
          .catch((error) => message.error(`Loaded values, but they were not saved to the server: ${error.message}`));
      } catch (error) {
        message.error(error.message || 'Could not read the current-year values CSV.');
      }
    };
    reader.onerror = () => message.error('Could not read the selected current-year CSV.');
    reader.readAsText(file);
    return false;
  };

  return { currentYearValues, handleCurrentYearUpload };
};

function TradeBuilder({ userID, players, marks, onConnectMfl, currentYearValues, handleCurrentYearUpload }) {
  const isMobile = useIsMobile();
  const [playerLimit, setPlayerLimit] = useState(MOBILE_PAGE_SIZE);
  const [leagues, setLeagues] = useState([]);
  const [leagueID, setLeagueID] = useState('');
  const [leagueLoading, setLeagueLoading] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('PLAYER');
  const [targetPlayerID, setTargetPlayerID] = useState('');
  const [targetFranchiseID, setTargetFranchiseID] = useState('');
  const [week, setWeek] = useState(1);
  const [assetSearch, setAssetSearch] = useState('');
  const [mineSelected, setMineSelected] = useState([]);
  const [theirsSelected, setTheirsSelected] = useState([]);
  const [assetTableSorts, setAssetTableSorts] = useState({});
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [sendingTrade, setSendingTrade] = useState(false);
  const [playerSearch, setPlayerSearch] = useState('');
  const [playerPosition, setPlayerPosition] = useState('ALL');
  const [playerMarketFilter, setPlayerMarketFilter] = useState('ALL');
  const [playerHorizonFilter, setPlayerHorizonFilter] = useState('ALL');

  useEffect(() => {
    let active = true;
    setLeagueLoading(true);
    fetch(`/api/waivers/myLeagues?userid=${encodeURIComponent(userID || 1)}`)
      .then((response) => response.json().then((payload) => ({ response, payload })))
      .then(({ response, payload }) => {
        if (!response.ok) throw new Error(payload.message || 'Unable to load leagues.');
        if (!active) return;
        const nextLeagues = Array.isArray(payload.response) ? payload.response : [];
        setLeagues(nextLeagues);
      })
      .catch((error) => message.error(error.message || 'Unable to load leagues.'))
      .finally(() => { if (active) setLeagueLoading(false); });
    return () => { active = false; };
  }, [userID]);

  const loadLeague = async (selectedLeague = leagueID, selectedWeek = week) => {
    if (!selectedLeague) return;
    setLoading(true);
    setData(null);
    try {
      const response = await fetch(`/api/trades/builderData?userID=${encodeURIComponent(userID || 1)}&leagueID=${encodeURIComponent(selectedLeague)}&year=2026&week=${selectedWeek}`);
      const payload = await response.json();
      if (response.status === 401 && payload.mflLoginRequired) {
        if (onConnectMfl) onConnectMfl();
        throw new Error('Connect MFL to load league rosters and projections.');
      }
      if (!response.ok) throw new Error(payload.message || 'Unable to load league trade data.');
      setData(payload.response);
      setTargetPlayerID('');
      setTargetFranchiseID('');
      setMineSelected([]);
      setTheirsSelected([]);
    } catch (error) {
      message.error(error.message || 'Unable to load league trade data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (leagueID) loadLeague(leagueID, week);
    // Load when the selected league changes; week changes require explicit refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueID]);

  const teams = data?.teams || [];
  const myTeam = teams.find((team) => team.isMine);
  const otherTeams = teams.filter((team) => !team.isMine);
  const targetTeam = otherTeams.find((team) => team.franchiseID === targetFranchiseID);
  const selectedTeam = targetTeam;
  const allTradeBaitIDs = useMemo(() => new Set((data?.tradeBaitPlayerIDs || []).map(normalizeID)), [data]);

  const rankedDynastyPlayers = useMemo(() => addMissingPositionRanks(players), [players]);
  const dynastyValuesByID = useMemo(() => {
    const result = new Map();
    rankedDynastyPlayers.forEach((player) => {
      const id = playerValueID(player);
      if (id && id !== 'unk') result.set(id, player);
    });
    return result;
  }, [rankedDynastyPlayers]);
  const dynastyPickValuesByYearRound = useMemo(() => {
    const result = new Map();
    rankedDynastyPlayers.forEach((player) => {
      const yearRound = pickYearRoundFromPlayer(player);
      if (yearRound && (String(player.position).toUpperCase() === 'PICK' || /^FP_/i.test(String(player.mflId || '')))) {
        const existing = result.get(yearRound);
        if (!existing || Number(player.value || 0) > Number(existing.value || 0)) result.set(yearRound, player);
      }
    });
    return result;
  }, [rankedDynastyPlayers]);
  const rankedCurrentYearPlayers = useMemo(
    () => addMissingPositionRanks(currentYearValues.players),
    [currentYearValues.players]
  );
  const currentValuesByID = useMemo(() => {
    const byID = new Map();
    const byName = new Map();
    rankedCurrentYearPlayers.forEach((player) => {
      const current = { value: player.value, positionRank: player.positionRank, overallRank: player.overallRank };
      const id = normalizeID(player.mflId);
      if (id && id !== 'unk') byID.set(id, current);
      byName.set(normalizeName(player.name), current);
    });
    return { byID, byName };
  }, [rankedCurrentYearPlayers]);
  const dynastyPositionCurves = useMemo(() => {
    const byPosition = new Map();
    rankedDynastyPlayers.forEach((player) => {
      const position = String(player.position || '').toUpperCase();
      if (!position || !Number.isFinite(Number(player.value))) return;
      if (!byPosition.has(position)) byPosition.set(position, []);
      byPosition.get(position).push(player);
    });
    byPosition.forEach((positionPlayers) => {
      positionPlayers.sort((a, b) => Number(a.positionRank || 99999) - Number(b.positionRank || 99999));
    });
    return byPosition;
  }, [rankedDynastyPlayers]);
  const playerRows = useMemo(() => {
    const rowsByID = new Map();
    rankedDynastyPlayers.forEach((player) => {
      const id = playerValueID(player);
      rowsByID.set(id && id !== 'unk' ? id : String(player.key), player);
    });
    teams.forEach((team) => (team.players || []).forEach((asset) => {
      const id = normalizeID(asset.playerID);
      if (allTradeBaitIDs.has(id) && !rowsByID.has(id)) {
        rowsByID.set(id, { key: id, name: asset.name, position: asset.position, team: asset.nflTeam, age: null, mflId: asset.playerID, value: 0, positionRank: null, overallRank: null });
      }
    }));
    return Array.from(rowsByID.values()).filter((player) => player.name).map((player) => {
      const assetID = playerValueID(player);
      const id = assetID && assetID !== 'unk' ? assetID : String(player.key);
      const current = (assetID && assetID !== 'unk' ? currentValuesByID.byID.get(assetID) : null) || currentValuesByID.byName.get(normalizeName(player.name));
      const rawCurrentYearValue = current?.value;
      const currentYearValue = rawCurrentYearValue != null && current?.positionRank
        ? normalizeCurrentYearValue(player.position, current.positionRank, dynastyPositionCurves)
        : null;
      const valueDifference = currentYearValue == null ? null : currentYearValue - Number(player.value || 0);
      const valueDifferencePercent = currentYearValue == null || !Number(player.value) ? null : valueDifference / Number(player.value);
      const localMark = marks[player.key] || '';
      const tradeBait = allTradeBaitIDs.has(assetID) || localMark === 'TRADEBAIT';
      return {
        ...player, id, currentYearValue, rawCurrentYearValue,
        currentYearPositionRank: current?.positionRank || null,
        valueDifference, valueDifferencePercent,
        horizon: horizonForPercent(valueDifferencePercent, player.value, player.position), localMark, tradeBait
      };
    }).sort((a, b) => Number(b.value || 0) - Number(a.value || 0));
  }, [rankedDynastyPlayers, teams, allTradeBaitIDs, currentValuesByID, marks, dynastyPositionCurves]);

  const filteredPlayerRows = useMemo(() => playerRows.filter((player) => {
    const text = `${player.name} ${player.position} ${player.team || ''}`.toLowerCase();
    const matchesSearch = !playerSearch || text.includes(playerSearch.toLowerCase());
    const matchesPosition = playerPosition === 'ALL' || String(player.position).toUpperCase() === playerPosition;
    const matchesMarket = playerMarketFilter === 'ALL'
      || (playerMarketFilter === 'BUY' && player.localMark === 'BUY')
      || (playerMarketFilter === 'SELL' && player.localMark === 'SELL')
      || (playerMarketFilter === 'TRADEBAIT' && player.tradeBait);
    const matchesHorizon = playerHorizonFilter === 'ALL' || player.horizon === playerHorizonFilter;
    return matchesSearch && matchesPosition && matchesMarket && matchesHorizon;
  }), [playerRows, playerSearch, playerPosition, playerMarketFilter, playerHorizonFilter]);

  const playerByID = useMemo(() => new Map(playerRows.map((player) => [normalizeID(player.mflId), player])), [playerRows]);
  useEffect(() => { setPlayerLimit(MOBILE_PAGE_SIZE); }, [playerSearch, playerPosition, playerMarketFilter, playerHorizonFilter]);
  const teamOverviewRows = useMemo(() => teams.map((team) => {
    const horizonCounts = Object.fromEntries(HORIZON_ORDER.map((horizon) => [horizon, 0]));
    const horizonValues = Object.fromEntries(HORIZON_ORDER.map((horizon) => [horizon, 0]));
    let noComparison = 0;
    let noComparisonValue = 0;
    let totalPlayerValue = 0;
    (team.players || []).forEach((asset) => {
      const player = playerByID.get(normalizeID(asset.playerID));
      const value = Math.max(Number(player?.value) || 0, 0);
      totalPlayerValue += value;
      if (!player || player.horizon === 'MISSING') {
        noComparison += 1;
        noComparisonValue += value;
      } else {
        horizonCounts[player.horizon] += 1;
        horizonValues[player.horizon] += value;
      }
    });
    const picks = team.picks || [];
    const allRosterAssets = [...(team.players || []), ...picks];
    allRosterAssets.forEach((asset) => {
      const horizon = asset.position === 'PICK' ? 'VERY_LONG' : null;
      if (!horizon) return;
      const valuedPick = dynastyValuesByID.get(assetValueID(asset)) || dynastyPickValuesByYearRound.get(pickYearRoundFromAsset(asset));
      const pickValue = Math.max(Number(valuedPick?.value) || 0, 0);
      horizonCounts[horizon] += 1;
      horizonValues[horizon] += pickValue;
      totalPlayerValue += pickValue;
    });
    const countRound = (round) => picks.filter((pick) => {
      const name = String(pick.name || '');
      const ordinal = name.match(/\b(1st|2nd|3rd|4th)\b/i)?.[1];
      const parsed = Number(pick.round) || Number(name.match(/round\s*(\d+)/i)?.[1]) || (ordinal ? Number(ordinal[0]) : 0);
      return parsed === round;
    }).length;
    const scoreRows = team.optimalScores || [];
    const horizonWeights = { VERY_LONG: -1, LONG: -0.5, SIMILAR: 0, SHORT: 0.5, VERY_SHORT: 1 };
    const shortTermScore = totalPlayerValue > 0
      ? Object.entries(horizonValues).reduce((sum, [horizon, value]) => sum + (horizonWeights[horizon] || 0) * value, 0) / totalPlayerValue
      : null;
    return {
      ...team,
      rosterCount: (team.players || []).length + picks.length,
      horizonCounts,
      horizonValues,
      totalPlayerValue,
      noComparison,
      noComparisonValue,
      shortTermScore,
      totalPicks: picks.length,
      firsts: countRound(1), seconds: countRound(2), thirds: countRound(3), fourths: countRound(4),
      averageProjection: scoreRows.length ? scoreRows.reduce((sum, score) => sum + (Number(score.score) || 0), 0) / scoreRows.length : null
    };
  }), [teams, playerByID, dynastyValuesByID, dynastyPickValuesByYearRound]);
  const rankedTeamRows = useMemo(() => {
    const sorted = teamOverviewRows.slice().sort((a, b) => {
      if (a.shortTermScore == null && b.shortTermScore != null) return 1;
      if (b.shortTermScore == null && a.shortTermScore != null) return -1;
      return (b.shortTermScore ?? 0) - (a.shortTermScore ?? 0)
        || Number(a.standings?.rank || 999) - Number(b.standings?.rank || 999)
        || a.name.localeCompare(b.name);
    });
    return sorted.map((team, index) => ({ ...team, shortTermRank: team.shortTermScore == null ? null : index + 1 }));
  }, [teamOverviewRows]);
  const rankedTeamValueRows = useMemo(() => {
    const byValue = teamOverviewRows.slice().sort((a, b) => b.totalPlayerValue - a.totalPlayerValue || a.name.localeCompare(b.name));
    const rankByFranchise = new Map(byValue.map((team, index) => [team.franchiseID, index + 1]));
    return rankedTeamRows.map((team) => ({ ...team, dynastyValueRank: rankByFranchise.get(team.franchiseID) || null }));
  }, [rankedTeamRows, teamOverviewRows]);
  const selectedTeamSummary = rankedTeamValueRows.find((team) => team.franchiseID === selectedTeam?.franchiseID);

  const targetPlayer = playerRows.find((player) => player.id === normalizeID(targetPlayerID));
  const ownerTeams = targetPlayer ? teams
    .filter((team) => (team.players || []).some((player) => normalizeID(player.playerID) === normalizeID(targetPlayerID)))
    .sort((a, b) => {
      const rankA = rankedTeamRows.find((team) => team.franchiseID === a.franchiseID)?.shortTermRank || 999;
      const rankB = rankedTeamRows.find((team) => team.franchiseID === b.franchiseID)?.shortTermRank || 999;
      return rankA - rankB;
    }) : [];
  const tradeMark = (asset) => {
    const player = dynastyValuesByID.get(assetValueID(asset));
    return player ? marks[player.key] || '' : '';
  };
  const getValuedAsset = (asset) => dynastyValuesByID.get(assetValueID(asset)) || dynastyPickValuesByYearRound.get(pickYearRoundFromAsset(asset));
  const tradeValue = (asset) => Number(getValuedAsset(asset)?.value || 0);
  const assetLabel = (asset) => asset.name || getValuedAsset(asset)?.name || `MFL asset ${asset.assetID}`;
  const filteredAssets = (team) => team ? [...(team.players || []), ...(team.picks || [])].filter((asset) => `${asset.name || ''} ${asset.position || ''} ${asset.nflTeam || ''}`.toLowerCase().includes(assetSearch.toLowerCase())) : [];
  const myAssets = [...(myTeam?.players || []), ...(myTeam?.picks || [])];
  const theirAssets = [...(selectedTeam?.players || []), ...(selectedTeam?.picks || [])];
  const mySelectedAssets = myAssets.filter((asset) => mineSelected.includes(asset.assetID));
  const theirSelectedAssets = theirAssets.filter((asset) => theirsSelected.includes(asset.assetID));
  const myTotal = mySelectedAssets.reduce((sum, asset) => sum + tradeValue(asset), 0);
  const theirTotal = theirSelectedAssets.reduce((sum, asset) => sum + tradeValue(asset), 0);
  const netValue = myTotal - theirTotal;
  const getTradePlayer = (asset) => playerByID.get(normalizeID(asset.playerID));
  const rankLabel = (asset, currentSeason = false) => {
    const player = getTradePlayer(asset);
    if (!player || asset.position === 'PICK') return null;
    const rank = currentSeason ? player.currentYearPositionRank : player.positionRank;
    return rank ? `${player.position}${rank}` : null;
  };

  const sendTrade = async () => {
    if (!leagueID || !selectedTeam || !mineSelected.length || !theirsSelected.length) {
      message.error('Select a partner and at least one asset for each side.');
      return;
    }
    setSendingTrade(true);
    try {
      const response = await fetch('/api/trades/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userID: userID || 1,
          year: 2026,
          leagueID,
          offeredTo: selectedTeam.franchiseID,
          giveUp: mineSelected,
          receive: theirsSelected
        })
      });
      const payload = await response.json();
      if (response.status === 401 && payload.mflLoginRequired) {
        setConfirmVisible(false);
        if (onConnectMfl) onConnectMfl();
        throw new Error('Connect MFL before sending the trade.');
      }
      if (!response.ok) throw new Error(payload.message || 'MFL could not submit the trade offer.');
      message.success('Trade offer sent to MFL.');
      setConfirmVisible(false);
      setMineSelected([]);
      setTheirsSelected([]);
    } catch (error) {
      message.error(error.message || 'Could not send the trade offer.');
    } finally {
      setSendingTrade(false);
    }
  };

  const teamStrategy = (team) => {
    if (!team) return null;
    const playerValues = (team.players || []).map((asset) => dynastyValuesByID.get(normalizeID(asset.playerID))).filter(Boolean);
    const ages = playerValues.map((player) => Number(player.age)).filter((age) => age > 18 && age < 50);
    const averageAge = ages.length ? ages.reduce((sum, age) => sum + age, 0) / ages.length : null;
    const pickAverage = teams.length ? teams.reduce((sum, item) => sum + (item.picks || []).length, 0) / teams.length : 0;
    const scores = (team.optimalScores || []).map((score) => Number(score.score) || 0);
    const averageScore = scores.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;
    const averageLeagueScore = teams.length ? teams.reduce((sum, item) => sum + (item.optimalScores || []).reduce((total, score) => total + (Number(score.score) || 0), 0) / Math.max((item.optimalScores || []).length, 1), 0) / teams.length : 0;
    const rank = Number(team.standings?.rank) || null;
    let score = 0;
    let factors = 0;
    if (averageAge != null) { score += (averageAge - 26) * 4; factors += 1; }
    if (rank) { score += ((teams.length + 1) / 2 - rank) * 5; factors += 1; }
    if (averageScore && averageLeagueScore) { score += (averageScore - averageLeagueScore) * 0.35; factors += 1; }
    if (pickAverage) { score += (pickAverage - (team.picks || []).length) * 3; factors += 1; }
    const normalized = factors ? score / factors : 0;
    return {
      label: normalized >= 2.5 ? 'Competing · short-term' : normalized <= -2.5 ? 'Rebuilding · long-term' : 'Balanced / unclear',
      rank, picks: (team.picks || []).length, pickAverage, averageScore, scores
    };
  };
  const outlook = teamStrategy(selectedTeam);

  const playerColumns = [
    { title: 'Player', dataIndex: 'name', key: 'name', render: (name, player) => <div className="trade-player"><Text strong>{name}</Text><Text type="secondary">{player.position}{player.team ? ` · ${player.team}` : ''}{player.age ? ` · ${player.age}` : ''}</Text></div> },
    { title: 'Dynasty value', dataIndex: 'value', key: 'value', width: 105, align: 'right', defaultSortOrder: 'descend', sorter: (a, b) => Number(a.value) - Number(b.value), render: formatValue },
    { title: 'Dynasty rank', dataIndex: 'positionRank', key: 'positionRank', width: 100, align: 'center', sorter: (a, b) => Number(b.positionRank || 9999) - Number(a.positionRank || 9999), render: (rank, player) => rank ? `${player.position}${rank}` : '—' },
    { title: 'This-year value*', dataIndex: 'currentYearValue', key: 'currentYearValue', width: 125, align: 'right', sorter: (a, b) => Number(a.currentYearValue || 0) - Number(b.currentYearValue || 0), render: (value, player) => value == null ? '—' : <Tooltip title={`Raw this-year CSV value: ${formatValue(player.rawCurrentYearValue)}. Normalized using dynasty values for players within ±2 ${player.position} positional ranks of ${player.currentYearPositionRank || 'the current-year rank'}.`}>{formatValue(value)}</Tooltip> },
    { title: 'This-year rank', dataIndex: 'currentYearPositionRank', key: 'currentYearPositionRank', width: 110, align: 'center', sorter: (a, b) => Number(b.currentYearPositionRank || 9999) - Number(a.currentYearPositionRank || 9999), render: (rank, player) => rank ? `${player.position}${rank}` : '—' },
    { title: 'Annual delta', dataIndex: 'valueDifferencePercent', key: 'valueDifferencePercent', width: 120, align: 'right', sorter: (a, b) => Number(a.valueDifferencePercent ?? -Infinity) - Number(b.valueDifferencePercent ?? -Infinity), render: (percent, player) => percent == null ? '—' : <span className={percent > 0 ? 'trade-net-positive' : percent < 0 ? 'trade-net-negative' : ''}>{percent > 0 ? '+' : ''}{(percent * 100).toFixed(0)}% <Text type="secondary">({percent > 0 ? '+' : ''}{formatValue(player.valueDifference)})</Text></span> },
    { title: 'Horizon', dataIndex: 'horizon', key: 'horizon', width: 150, filters: HORIZON_ORDER.map((value) => ({ text: HORIZON_LABELS[value], value })).concat([{ text: HORIZON_LABELS.MISSING, value: 'MISSING' }]), onFilter: (value, player) => player.horizon === value, render: (_, player) => <HorizonTag horizon={player.horizon} /> },
    { title: 'Market', key: 'market', width: 160, render: (_, player) => <Space size={[2, 2]} wrap>{player.localMark === 'BUY' && <Tag color="green">BUY</Tag>}{player.localMark === 'SELL' && <Tag color="red">SELL</Tag>}{player.tradeBait && <Tag color="purple">TRADE BAIT</Tag>}{!player.localMark && !player.tradeBait && <Text type="secondary">—</Text>}</Space> },
    { title: '', key: 'select', width: 90, render: (_, player) => <Button size="small" type={targetPlayerID === player.id ? 'primary' : 'default'} onClick={() => { setTargetPlayerID(player.id); setTargetFranchiseID(''); setMineSelected([]); setTheirsSelected([]); }}>{targetPlayerID === player.id ? 'Selected' : 'Select'}</Button> }
  ];

  const teamColumns = [
    { title: 'Team', dataIndex: 'name', key: 'name', fixed: 'left', width: 190, sorter: (a, b) => a.name.localeCompare(b.name), render: (name, team) => <Space><Text strong>{name}</Text>{team.isMine && <Tag color="blue">MY TEAM</Tag>}</Space> },
    { title: 'Dynasty value rank (value)', dataIndex: 'totalPlayerValue', key: 'totalPlayerValue', width: 175, align: 'right', sorter: (a, b) => a.totalPlayerValue - b.totalPlayerValue, defaultSortOrder: 'descend', render: (value, team) => <Text strong>#{team.dynastyValueRank || '—'} ({formatValue(value)})</Text> },
    { title: 'Short-term rank', dataIndex: 'shortTermRank', key: 'shortTermRank', width: 120, align: 'center', sorter: (a, b) => (a.shortTermRank || 999) - (b.shortTermRank || 999), render: (rank) => rank ? <Tag color={rank <= Math.ceil(teams.length / 3) ? 'orange' : 'default'}>#{rank}</Tag> : '—' },
    { title: 'Standing', key: 'standing', width: 85, align: 'center', sorter: (a, b) => Number(a.standings?.rank || 999) - Number(b.standings?.rank || 999), render: (_, team) => team.standings?.rank ? `#${team.standings.rank}` : '—' },
    ...HORIZON_ORDER.map((horizon) => ({ title: HORIZON_LABELS[horizon], key: horizon, width: 130, align: 'center', sorter: (a, b) => a.shortTermScore - b.shortTermScore || a.horizonValues[horizon] - b.horizonValues[horizon], render: (_, team) => <span>{team.horizonCounts[horizon]} <Text type="secondary">({team.totalPlayerValue ? Math.round(team.horizonValues[horizon] / team.totalPlayerValue * 100) : 0}% value)</Text></span> })),
    { title: 'No comparison', key: 'noComparison', width: 125, align: 'center', sorter: (a, b) => a.noComparisonValue - b.noComparisonValue, render: (_, team) => <span>{team.noComparison} <Text type="secondary">({team.totalPlayerValue ? Math.round(team.noComparisonValue / team.totalPlayerValue * 100) : 0}% value)</Text></span> },
    { title: 'Picks', dataIndex: 'totalPicks', key: 'totalPicks', width: 70, align: 'center', sorter: (a, b) => a.totalPicks - b.totalPicks },
    { title: '1sts', dataIndex: 'firsts', key: 'firsts', width: 60, align: 'center', sorter: (a, b) => a.firsts - b.firsts },
    { title: '2nds', dataIndex: 'seconds', key: 'seconds', width: 60, align: 'center', sorter: (a, b) => a.seconds - b.seconds },
    { title: '3rds', dataIndex: 'thirds', key: 'thirds', width: 60, align: 'center', sorter: (a, b) => a.thirds - b.thirds },
    { title: '4ths', dataIndex: 'fourths', key: 'fourths', width: 60, align: 'center', sorter: (a, b) => a.fourths - b.fourths },
    { title: 'Avg. optimal · next 3 weeks', dataIndex: 'averageProjection', key: 'averageProjection', width: 175, align: 'right', sorter: (a, b) => Number(a.averageProjection || 0) - Number(b.averageProjection || 0), render: (value) => value == null ? '—' : Number(value).toFixed(1) },
    { title: '', key: 'select', fixed: 'right', width: 95, render: (_, team) => team.isMine ? <Text type="secondary">Your team</Text> : <Button size="small" type={team.franchiseID === targetFranchiseID ? 'primary' : 'default'} onClick={() => { setTargetFranchiseID(team.franchiseID); setTheirsSelected([]); setMineSelected([]); }}>{team.franchiseID === targetFranchiseID ? 'Selected' : 'Select'}</Button> }
  ];

  const renderAssets = (team, selected, setSelected) => {
    const teamKey = team?.franchiseID || '';
    const sort = assetTableSorts[teamKey] || { key: 'value', order: 'descend' };
    const valueSort = (a, b) => tradeValue(a) - tradeValue(b);
    const positionOrder = ['QB', 'RB', 'WR', 'TE', 'DEF', 'PICK'];
    const comparePosition = (a, b) => {
      const positionA = positionOrder.indexOf(String(a.position || 'PICK').toUpperCase());
      const positionB = positionOrder.indexOf(String(b.position || 'PICK').toUpperCase());
      return (positionA < 0 ? positionOrder.length : positionA) - (positionB < 0 ? positionOrder.length : positionB);
    };
    const compareHorizon = (a, b) => {
      const horizonA = playerByID.get(normalizeID(a.playerID))?.horizon || 'MISSING';
      const horizonB = playerByID.get(normalizeID(b.playerID))?.horizon || 'MISSING';
      const orderA = HORIZON_ORDER.indexOf(horizonA);
      const orderB = HORIZON_ORDER.indexOf(horizonB);
      return (orderA < 0 ? HORIZON_ORDER.length : orderA) - (orderB < 0 ? HORIZON_ORDER.length : orderB);
    };
    const rows = filteredAssets(team).slice().sort((a, b) => {
      let primary = 0;
      if (sort.key === 'position') primary = comparePosition(a, b);
      else if (sort.key === 'horizon') primary = compareHorizon(a, b);
      else if (sort.key === 'asset') primary = assetLabel(a).localeCompare(assetLabel(b));
      else primary = valueSort(a, b);
      if (sort.order === 'descend') primary *= -1;
      return primary || (sort.key === 'value' ? 0 : -valueSort(a, b));
    });
    const selectSort = (key) => ({
      sorter: true,
      sortOrder: sort.key === key ? sort.order : null,
      sortDirections: ['ascend', 'descend'],
      showSorterTooltip: false
    });
    const columns = [
      { title: 'Asset', dataIndex: 'name', key: 'asset', width: 150, ...selectSort('asset'), render: (_, asset) => <div className="trade-player"><Text strong>{assetLabel(asset)}</Text><Text type="secondary">{asset.position === 'PICK' ? 'Draft pick' : `${asset.position || ''}${asset.nflTeam ? ` · ${asset.nflTeam}` : ''}`}</Text></div> },
      { title: 'Pos', key: 'position', width: 62, ...selectSort('position'), render: (_, asset) => asset.position === 'PICK' ? <Tag>Pick</Tag> : <Tag className={`position-tag pos-${String(asset.position || '').toLowerCase()}`}>{asset.position || '—'}</Tag> },
      { title: 'Horizon', key: 'horizon', width: 122, ...selectSort('horizon'), render: (_, asset) => asset.position === 'PICK' ? <Text type="secondary">—</Text> : <HorizonTag horizon={playerByID.get(normalizeID(asset.playerID))?.horizon || 'MISSING'} /> },
      { title: 'Ranks', key: 'ranks', width: 125, render: (_, asset) => <Space size={2} wrap>{rankLabel(asset) && <Tag>Dynasty {rankLabel(asset)}</Tag>}{rankLabel(asset, true) && <Tag color="blue">Year {rankLabel(asset, true)}</Tag>}</Space> },
      { title: 'Market', key: 'market', width: 98, render: (_, asset) => { const mark = tradeMark(asset); const id = normalizeID(asset.playerID); const isBait = (team?.tradeBaitPlayerIDs || []).map(normalizeID).includes(id) || allTradeBaitIDs.has(id); return <Space size={1} wrap>{mark === 'BUY' && <Tag color="green">BUY</Tag>}{mark === 'SELL' && <Tag color="red">SELL</Tag>}{mark === 'TRADEBAIT' && <Tag color="gold">MY BAIT</Tag>}{isBait && <Tag color="purple">BAIT</Tag>}</Space>; } },
      { title: 'Value', key: 'value', width: 75, align: 'right', ...selectSort('value'), render: (_, asset) => <Text strong>{formatValue(tradeValue(asset))}</Text> }
    ];
    if (isMobile) {
      const toggleAsset = (assetID) => setSelected(selected.includes(assetID) ? selected.filter((id) => id !== assetID) : [...selected, assetID]);
      return <div className="builder-mobile-assets">
        <Select size="large" value={`${sort.key}:${sort.order}`} aria-label="Sort assets" onChange={(value) => { const [key, order] = value.split(':'); setAssetTableSorts((previous) => ({ ...previous, [teamKey]: { key, order } })); }}>
          {Object.entries(ASSET_SORT_OPTIONS).map(([value, label]) => <Select.Option key={value} value={value}>{label}</Select.Option>)}
        </Select>
        <div className="builder-mobile-asset-list">
          {rows.length ? rows.map((asset) => {
            const horizon = playerByID.get(normalizeID(asset.playerID))?.horizon || 'MISSING';
            const mark = tradeMark(asset);
            const id = normalizeID(asset.playerID);
            const isBait = (team?.tradeBaitPlayerIDs || []).map(normalizeID).includes(id) || allTradeBaitIDs.has(id);
            const checked = selected.includes(asset.assetID);
            return <div className={`builder-mobile-asset${checked ? ' is-selected' : ''}`} key={asset.assetID} onClick={() => toggleAsset(asset.assetID)}>
              <Checkbox checked={checked} />
              <div className="builder-mobile-asset-body">
                <div className="builder-mobile-asset-top"><div className="trade-player"><Text strong>{assetLabel(asset)}</Text><Text type="secondary">{asset.position === 'PICK' ? 'Draft pick' : `${asset.position || ''}${asset.nflTeam ? ` · ${asset.nflTeam}` : ''}`}</Text></div><Text strong>{formatValue(tradeValue(asset))}</Text></div>
                <div className="builder-mobile-tags">
                  {asset.position !== 'PICK' && <HorizonTag horizon={horizon} />}
                  {rankLabel(asset) && <Tag>Dynasty {rankLabel(asset)}</Tag>}
                  {rankLabel(asset, true) && <Tag color="blue">Year {rankLabel(asset, true)}</Tag>}
                  {mark === 'BUY' && <Tag color="green">BUY</Tag>}{mark === 'SELL' && <Tag color="red">SELL</Tag>}{mark === 'TRADEBAIT' && <Tag color="gold">MY BAIT</Tag>}{isBait && <Tag color="purple">BAIT</Tag>}
                </div>
              </div>
            </div>;
          }) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No matching assets" />}
        </div>
      </div>;
    }
    return <Table
      className="builder-asset-table"
      rowKey="assetID"
      size="small"
      columns={columns}
      dataSource={rows}
      rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys) }}
      pagination={false}
      tableLayout="fixed"
      scroll={{ y: 360 }}
      onChange={(_, __, sorter) => {
        const activeSort = Array.isArray(sorter) ? sorter.find((item) => item.order) : sorter;
        setAssetTableSorts((previous) => ({ ...previous, [teamKey]: activeSort?.order ? { key: activeSort.columnKey, order: activeSort.order } : { key: 'value', order: 'descend' } }));
      }}
      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No matching assets" /> }}
    />;
  };

  const targetRowSelection = {
    type: 'radio',
    selectedRowKeys: targetPlayerID ? [targetPlayerID] : [],
    onChange: (keys) => { setTargetPlayerID(keys[0] || ''); setTargetFranchiseID(''); setMineSelected([]); setTheirsSelected([]); }
  };

  return <div className="trade-builder">
    <div className="builder-controls">
      <Select showSearch optionFilterProp="children" value={leagueID || undefined} loading={leagueLoading} placeholder="Select a league" onChange={(value) => { setLeagueID(value); setData(null); }}>
        {leagues.map((league) => <Select.Option key={league.leagueID} value={String(league.leagueID)}>{league.leagueName}</Select.Option>)}
      </Select>
      <Radio.Group value={mode} onChange={(event) => { setMode(event.target.value); setTargetPlayerID(''); setTargetFranchiseID(''); }} buttonStyle="solid"><Radio.Button value="PLAYER">Target a player</Radio.Button><Radio.Button value="TEAM">Target a team</Radio.Button></Radio.Group>
      <Select value={week} onChange={setWeek} aria-label="Projection starting week" style={{ width: 145 }}>{Array.from({ length: 21 }, (_, index) => index + 1).map((value) => <Select.Option key={value} value={value}>Week {value}+</Select.Option>)}</Select>
      <Button icon={<ReloadOutlined />} loading={loading} disabled={!leagueID} onClick={() => loadLeague(leagueID, week)}>Load / refresh</Button>
    </div>
    {!players.length && <Alert showIcon type="warning" message="Player values are not loaded" description="Upload the dynasty FantasyCalc values CSV in Player Values to see trade balances." style={{ marginBottom: 14 }} />}
    {data && <>
      <div className="builder-league-caption"><Text strong>{data.leagueName}</Text><Text type="secondary">{teams.length} teams · Live MFL assets · {data.tradeBaitPlayerIDs?.length || 0} MFL trade-bait players · Projections for weeks {(data.weeks || []).join(', ')}</Text></div>
      {data.tradeBaitStatus?.error && <Alert showIcon type="warning" message="Could not load MFL trade bait" description={data.tradeBaitStatus.error} style={{ marginBottom: 12 }} />}
      {!data.tradeBaitStatus?.error && data.tradeBaitStatus?.count === 0 && <Alert showIcon type="warning" message="MFL trade-bait export returned no recognized players" description={`MFL response fields: ${(data.tradeBaitStatus.payloadKeys || []).join(', ') || 'none'}. Local Trade Bait marks are still shown.`} style={{ marginBottom: 12 }} />}
      {mode === 'PLAYER' ? <Card size="small" className="builder-step-card" title="1 · Select a player from values, watchlists, or MFL trade bait">
        <div className="builder-current-values-toolbar"><div><Text strong>Compare dynasty and this-year value</Text><Text type="secondary">{currentYearValues.fileName || 'Upload a separate current-year values CSV to classify horizons.'}</Text></div><Upload accept=".csv,text/csv" showUploadList={false} beforeUpload={handleCurrentYearUpload}><Button icon={<UploadOutlined />}>{currentYearValues.fileName ? 'Replace this-year CSV' : 'Upload this-year values CSV'}</Button></Upload></div>
        {currentYearValues.fileName && <Alert className="builder-horizon-note" showIcon type="info" message="Horizon bands use dynasty-scale normalized current-year value" description="Each current-year positional rank is mapped to the average dynasty value of players within ±2 ranks at that position. Delta = (normalized current-year value − dynasty value) ÷ dynasty value. ≤ −40% very long-term · −40% to −20% long-term · −20% to +20% similar · +20% to +40% short-term · ≥ +40% super short-term." />}
        <div className="builder-player-filters">
          <Input allowClear value={playerSearch} onChange={(event) => setPlayerSearch(event.target.value)} placeholder="Search all players" />
          <Select value={playerPosition} onChange={setPlayerPosition} aria-label="Filter player position"><Select.Option value="ALL">All positions</Select.Option>{['QB', 'RB', 'WR', 'TE'].map((position) => <Select.Option key={position} value={position}>{position}</Select.Option>)}</Select>
          <Select value={playerMarketFilter} onChange={setPlayerMarketFilter} aria-label="Filter player market"><Select.Option value="ALL">All market statuses</Select.Option><Select.Option value="BUY">Buy list</Select.Option><Select.Option value="SELL">Sell list</Select.Option><Select.Option value="TRADEBAIT">Trade bait</Select.Option></Select>
          <Select value={playerHorizonFilter} onChange={setPlayerHorizonFilter} aria-label="Filter player horizon"><Select.Option value="ALL">All horizons</Select.Option>{HORIZON_ORDER.map((horizon) => <Select.Option key={horizon} value={horizon}>{HORIZON_LABELS[horizon]}</Select.Option>)}<Select.Option value="MISSING">No comparison</Select.Option></Select>
        </div>
        <Text className="builder-table-count" type="secondary">Showing {filteredPlayerRows.length.toLocaleString()} of {playerRows.length.toLocaleString()} players · sorted by dynasty value high to low; annual delta sorts by percentage</Text>
        {isMobile ? <div className="trade-card-list builder-mobile-picker">
          {filteredPlayerRows.slice(0, playerLimit).map((player) => {
            const selectedPlayer = targetPlayerID === player.id;
            const percent = player.valueDifferencePercent;
            return <div className={`trade-card${selectedPlayer ? ' is-selected' : ''}`} key={player.id} onClick={() => targetRowSelection.onChange([player.id])}>
              <div className="trade-card-main">
                <div className="trade-card-info"><Text strong>{player.name}</Text><Text type="secondary">{player.position}{player.team ? ` · ${player.team}` : ''}{player.age ? ` · ${player.age}` : ''}{player.positionRank ? ` · ${player.position}${player.positionRank}` : ''}</Text></div>
                <div className="trade-card-value"><Text strong>{formatValue(player.value)}</Text>{player.currentYearValue != null && <Text type="secondary" style={{ fontSize: 12 }}>Year {formatValue(player.currentYearValue)}</Text>}</div>
              </div>
              <div className="builder-mobile-tags">
                <HorizonTag horizon={player.horizon} />
                {percent != null && <span className={percent > 0 ? 'trade-net-positive' : percent < 0 ? 'trade-net-negative' : ''}>{percent > 0 ? '+' : ''}{(percent * 100).toFixed(0)}%</span>}
                {player.localMark === 'BUY' && <Tag color="green">BUY</Tag>}{player.localMark === 'SELL' && <Tag color="red">SELL</Tag>}{player.tradeBait && <Tag color="purple">BAIT</Tag>}
                <Button size="small" className="builder-mobile-select" type={selectedPlayer ? 'primary' : 'default'}>{selectedPlayer ? 'Selected' : 'Select'}</Button>
              </div>
            </div>;
          })}
          {!filteredPlayerRows.length && <div className="trade-card-empty"><Text type="secondary">No players match these filters.</Text></div>}
          {playerLimit < filteredPlayerRows.length && <Button block size="large" onClick={() => setPlayerLimit((limit) => limit + MOBILE_PAGE_SIZE)}>Show more ({(filteredPlayerRows.length - playerLimit).toLocaleString()} left)</Button>}
        </div> : <Table rowKey="id" size="small" columns={playerColumns} dataSource={filteredPlayerRows} rowSelection={targetRowSelection} onRow={(player) => ({ onClick: () => targetRowSelection.onChange([player.id]) })} pagination={false} scroll={{ x: true, y: 420 }} locale={{ emptyText: 'No players match these filters.' }} />}
        {targetPlayerID && <div className="builder-owners"><Text strong>2 · Teams that own this player (short-term rank)</Text>{ownerTeams.length ? <Space wrap>{ownerTeams.map((team) => { const ownerRanked = rankedTeamValueRows.find((row) => row.franchiseID === team.franchiseID); return <Button key={team.franchiseID} type={team.franchiseID === targetFranchiseID ? 'primary' : 'default'} disabled={team.isMine} onClick={() => { setTargetFranchiseID(team.franchiseID); const owned = team.players.find((player) => normalizeID(player.playerID) === normalizeID(targetPlayerID)); setTheirsSelected(owned ? [owned.assetID] : []); }}>{team.name}{team.isMine ? ' · You' : ''}{team.standings?.rank ? ` · Standing #${team.standings.rank}` : ''}{ownerRanked?.shortTermRank ? ` · Short-term #${ownerRanked.shortTermRank}` : ''}{ownerRanked?.dynastyValueRank ? ` · Value #${ownerRanked.dynastyValueRank} (${formatValue(ownerRanked.totalPlayerValue)})` : ''}</Button>; })}</Space> : <Text type="secondary">This player is not rostered in the selected league.</Text>}</div>}
      </Card> : <Card size="small" className="builder-step-card" title="1 · Compare teams and select a trade partner"><Text type="secondary">Short-term rank uses the roster’s mix of short-term, similar, and long-term horizon value. Dynasty value rank sums dynasty values for rostered players and picks.</Text>{isMobile ? <div className="trade-card-list builder-mobile-picker">{rankedTeamValueRows.map((team) => {
        const selectedTeamCard = team.franchiseID === targetFranchiseID;
        return <div className={`trade-card${selectedTeamCard ? ' is-selected' : ''}`} key={team.franchiseID} onClick={() => { if (!team.isMine) { setTargetFranchiseID(team.franchiseID); setMineSelected([]); setTheirsSelected([]); } }}>
          <div className="trade-card-main">
            <div className="trade-card-info"><Text strong>{team.name}</Text><Text type="secondary">Standing {team.standings?.rank ? `#${team.standings.rank}` : '—'}{team.averageProjection != null ? ` · Proj ${Number(team.averageProjection).toFixed(1)}` : ''}</Text></div>
            {team.isMine ? <Tag color="blue">MY TEAM</Tag> : <Button size="small" type={selectedTeamCard ? 'primary' : 'default'}>{selectedTeamCard ? 'Selected' : 'Select'}</Button>}
          </div>
          <div className="builder-mobile-stats">
            <div><Text type="secondary">Dynasty value</Text><Text strong>#{team.dynastyValueRank || '—'} · {formatValue(team.totalPlayerValue)}</Text></div>
            <div><Text type="secondary">Short-term</Text><Text strong>{team.shortTermRank ? `#${team.shortTermRank}` : '—'}</Text></div>
            <div><Text type="secondary">Picks</Text><Text strong>{team.totalPicks} ({team.firsts} 1st · {team.seconds} 2nd)</Text></div>
          </div>
        </div>;
      })}</div> : <Table rowKey="franchiseID" size="small" columns={teamColumns} dataSource={rankedTeamValueRows} pagination={false} scroll={{ x: 1550 }} onRow={(team) => ({ onClick: () => { if (!team.isMine) { setTargetFranchiseID(team.franchiseID); setMineSelected([]); setTheirsSelected([]); } } })} rowClassName={(team) => team.franchiseID === targetFranchiseID ? 'builder-selected-team-row' : ''} />}</Card>}
      {selectedTeam && myTeam && <>
        <Card size="small" className="builder-strategy-card" title={<Space><span>Team outlook</span><Tag color={outlook.label.includes('Competing') ? 'green' : outlook.label.includes('Rebuilding') ? 'blue' : 'gold'}>{outlook.label}</Tag></Space>}>
          <Row gutter={[16, 10]}><Col xs={12} md={6}><Statistic title="Standing" value={outlook.rank ? `#${outlook.rank} of ${teams.length}` : '—'} /></Col><Col xs={12} md={6}><Statistic title="Future picks" value={outlook.picks} suffix={outlook.pickAverage ? `(league avg ${outlook.pickAverage.toFixed(1)})` : ''} /></Col><Col xs={12} md={6}><Statistic title="3-week optimal avg" value={outlook.averageScore ? outlook.averageScore.toFixed(1) : '—'} /></Col></Row>
          <div className="builder-team-horizon-summary"><Text strong>Team value by horizon</Text>{HORIZON_ORDER.map((horizon) => <Tag key={horizon}>{HORIZON_LABELS[horizon]}: {selectedTeamSummary?.horizonCounts[horizon] || 0} players · {selectedTeamSummary?.totalPlayerValue ? Math.round(selectedTeamSummary.horizonValues[horizon] / selectedTeamSummary.totalPlayerValue * 100) : 0}% value</Tag>)}<Tag>No comparison: {selectedTeamSummary?.noComparison || 0} · {selectedTeamSummary?.totalPlayerValue ? Math.round(selectedTeamSummary.noComparisonValue / selectedTeamSummary.totalPlayerValue * 100) : 0}% value</Tag></div>
          <div className="builder-projection-strip">{(selectedTeam.optimalScores || []).map((score) => <Tag key={score.week}>W{score.week}: {Number(score.score).toFixed(1)}</Tag>)}<Text type="secondary">Outlook uses roster age, relative pick count, standings, and projected lineup strength.</Text></div>
        </Card>
        <div className="builder-assets-toolbar"><Title level={5}>2 · Build the offer</Title><Input allowClear value={assetSearch} onChange={(event) => setAssetSearch(event.target.value)} placeholder="Filter players / picks" /></div>
        <Row gutter={[14, 14]}><Col xs={24} lg={12}><Card size="small" title={`You give · ${myTeam.name}`}>{renderAssets(myTeam, mineSelected, setMineSelected)}</Card></Col><Col xs={24} lg={12}><Card size="small" title={`You receive · ${selectedTeam.name}`}>{renderAssets(selectedTeam, theirsSelected, setTheirsSelected)}</Card></Col></Row>
        <Card size="small" className="builder-balance-card"><Row gutter={12} align="middle"><Col xs={8}><Statistic title="You give" value={formatValue(myTotal)} /></Col><Col xs={8}><Statistic title="You receive" value={formatValue(theirTotal)} /></Col><Col xs={8}><Statistic title="Net to you" value={`${netValue > 0 ? '+' : ''}${formatValue(netValue)}`} valueStyle={{ color: netValue >= 0 ? '#16845b' : '#c43d52' }} /></Col></Row><Button type="primary" icon={<SwapOutlined />} disabled={!mineSelected.length || !theirsSelected.length} onClick={() => setConfirmVisible(true)} style={{ marginTop: 12 }}>Review and send offer</Button></Card>
      </>}
    </>}
    {!data && !loading && !leagueLoading && <div className="builder-start-empty"><Empty description={leagueID ? 'Choose a trade approach above to get started.' : 'Select a league to start building a trade.'} /></div>}
    <Modal title="Confirm trade offer" visible={confirmVisible} onCancel={() => setConfirmVisible(false)} onOk={sendTrade} confirmLoading={sendingTrade} okText="Send offer to MFL" okButtonProps={{ danger: true }} width={700}>
      <Alert showIcon type="warning" message={`You are about to send an offer to ${selectedTeam?.name || 'the selected team'} in ${data?.leagueName || 'this league'}.`} description="This sends the trade offer to MFL and notifies the other manager. Check both sides before confirming." />
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={12}><Card size="small" title={`You give · ${formatValue(myTotal)}`}>{mySelectedAssets.map((asset) => <div className="builder-confirm-asset" key={asset.assetID}><Text>{assetLabel(asset)}</Text><Text strong>{formatValue(tradeValue(asset))}</Text></div>)}</Card></Col>
        <Col xs={24} md={12}><Card size="small" title={`You receive · ${formatValue(theirTotal)}`}>{theirSelectedAssets.map((asset) => <div className="builder-confirm-asset" key={asset.assetID}><Text>{assetLabel(asset)}</Text><Text strong>{formatValue(tradeValue(asset))}</Text></div>)}</Card></Col>
      </Row>
      <div className="builder-confirm-net"><Text strong>Net value to you</Text><Text strong className={netValue >= 0 ? 'trade-net-positive' : 'trade-net-negative'}>{netValue > 0 ? '+' : ''}{formatValue(netValue)}</Text></div>
    </Modal>
  </div>;
}

export default TradeBuilder;
